/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  authAPI,
  User,
  Tenant,
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  APIError,
} from '../services/api';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isAuthenticated as checkIsAuthenticated,
  isTokenExpiringSoon,
} from '../services/auth';

// Context state interface
interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Context methods interface
interface AuthContextMethods {
  login: (email: string, password: string) => Promise<void>;
  register: (businessName: string, email: string, password: string, industryType: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

// Combined context type
interface AuthContextType extends AuthState, AuthContextMethods {}

// Create context with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access authentication context
 * Must be used within an AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to children
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Computed authentication status
  const isAuthenticated = useMemo(() => {
    return user !== null && checkIsAuthenticated();
  }, [user]);

  /**
   * Clear any existing error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle successful authentication response
   */
  const handleAuthSuccess = useCallback((response: AuthResponse) => {
    const { user: userData, tenant: tenantData, tokens } = response;
    setTokens(tokens.access_token, tokens.refresh_token);
    setUser(userData);
    setTenant(tenantData);
    setError(null);
  }, []);

  /**
   * Refresh token
   */
  const refreshToken = useCallback(async () => {
    const token = getRefreshToken();
    if (!token) {
      throw new Error('No refresh token available');
    }

    try {
      const tokenResponse = await authAPI.refresh(token);
      setTokens(tokenResponse.access_token, tokenResponse.refresh_token);
    } catch (err) {
      clearTokens();
      setUser(null);
      setTenant(null);
      throw err;
    }
  }, []);

  /**
   * Refresh user data from the server
   */
  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setTenant(null);
      setIsLoading(false);
      return;
    }

    try {
      // Check if token is expiring soon and refresh if needed
      if (isTokenExpiringSoon(5 * 60 * 1000)) {
        // 5 minutes
        const refreshTokenStr = getRefreshToken();
        if (refreshTokenStr) {
          try {
            const tokenResponse = await authAPI.refresh(refreshTokenStr);
            setTokens(tokenResponse.access_token, tokenResponse.refresh_token);
          } catch (refreshError) {
            // If refresh fails, continue trying to get current user
            console.warn('Token refresh failed during user refresh');
          }
        }
      }

      const response = await authAPI.getCurrentUser();
      setUser(response.user);
      setTenant(response.tenant);
      setError(null);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      // Clear state on auth error
      clearTokens();
      setUser(null);
      setTenant(null);

      if (err instanceof APIError && err.status !== 401) {
        setError(err.message);
      }
    }
  }, []);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        await refreshUser();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [refreshUser]);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const data: LoginRequest = { email, password };
        const response = await authAPI.login(data);
        handleAuthSuccess(response);
      } catch (err) {
        const message = err instanceof APIError ? err.message : 'Login failed. Please try again.';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthSuccess]
  );

  /**
   * Register a new account
   */
  const register = useCallback(
    async (businessName: string, email: string, password: string, industryType: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const data: RegisterRequest = {
          business_name: businessName,
          email,
          password,
          industry_type: industryType,
        };
        const response = await authAPI.register(data);
        handleAuthSuccess(response);
      } catch (err) {
        const message =
          err instanceof APIError ? err.message : 'Registration failed. Please try again.';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthSuccess]
  );

  /**
   * Logout the current user
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authAPI.logout();
    } catch (err) {
      // Ignore logout errors - we'll clear local state anyway
      console.warn('Logout request failed:', err);
    } finally {
      clearTokens();
      setUser(null);
      setTenant(null);
      setIsLoading(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      tenant,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshUser,
      refreshToken,
      clearError,
    }),
    [user, tenant, isAuthenticated, isLoading, error, login, register, logout, refreshUser, refreshToken, clearError]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthContext;
